import { useCallback, type ReactNode } from 'react';
import { useDebouncedAutosave } from '@/hooks/useDebouncedAutosave';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { upsertQariDraft } from '@/lib/queries/qari';
import { cn } from '@/lib/utils/cn';
import {
  QARI_TEACHING_STYLE_LABELS,
  type QariProfile,
  type QariProfileDraft,
  type QariTeachingStyle,
} from '@/types/qari';
import {
  Field,
  SelectInput,
  TextArea,
  WizardShell,
  nullableText,
  renderDraftGuard,
  useQariDraftProfile,
} from '@/components/qari/wizard';
import { IconShield, IconSparkle } from '@/components/home/icons';

/**
 * /qari/onboarding/expertise — Step 2: Academic & Spiritual Expertise.
 *
 * Reuses the shared wizard shell. Back links to /qari/onboarding/identity;
 * Next is disabled until the Step 3 design arrives — work is autosaved
 * regardless of step.
 */
export function QariOnboarding3() {
  useDocumentTitle('Qari Application · Expertise');
  const draft = useQariDraftProfile();
  const guard = renderDraftGuard(draft);
  if (guard) return guard;
  // See QariOnboarding2 — defensive narrow so TS knows draft is in the
  // 'ready' phase after renderDraftGuard returns null.
  if (draft.phase !== 'ready') return null;
  return <Step profile={draft.profile} userId={draft.userId} />;
}

/* ---- Field options ---- */

const PRIMARY_MASTERY: { value: string; label: string }[] = [
  { value: 'tajweed', label: 'Tajweed (Rules of Recitation)' },
  { value: 'hifz', label: 'Hifz (Memorization)' },
  { value: 'tafsir', label: 'Tafsir (Exegesis)' },
  { value: 'qiraat', label: "Qira'at (The Ten Readings)" },
  { value: 'tilawah', label: 'Tilawah (Recitation)' },
  { value: 'arabic', label: 'Arabic Language' },
];

const YEARS_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '1–3 Years' },
  { value: 3, label: '3–5 Years' },
  { value: 5, label: '5–10 Years' },
  { value: 10, label: '10+ Years' },
];

const TEACHING_STYLE_ORDER: QariTeachingStyle[] = [
  'traditional',
  'academic',
  'child_friendly',
  'accelerated',
  'foundational',
];

const SPECIALIZATIONS: string[] = [
  'Tajweed',
  'Hifz',
  'Tafsir',
  "Qira'at",
  'Tilawah',
  'Arabic Language',
];

const CERT_MAX = 2000;

type FormValue = Partial<QariProfileDraft>;

function Step({
  profile,
  userId,
}: {
  profile: QariProfile;
  userId: string;
}) {
  const save = useCallback(
    async (v: FormValue) => {
      await upsertQariDraft(userId, normalizePatch(v));
    },
    [userId],
  );
  const form = useDebouncedAutosave<FormValue>({
    initialValue: toFormValue(profile),
    save,
  });

  function toggleStyle(s: QariTeachingStyle) {
    const cur = form.value.teaching_styles ?? [];
    form.patch({
      teaching_styles: cur.includes(s)
        ? cur.filter((x) => x !== s)
        : [...cur, s],
    });
  }

  function toggleSpecialization(s: string) {
    const cur = form.value.specializations ?? [];
    form.patch({
      specializations: cur.includes(s)
        ? cur.filter((x) => x !== s)
        : [...cur, s],
    });
  }

  return (
    <WizardShell
      currentStep={2}
      title="Step 2: Academic & Spiritual Expertise"
      heading="Academic & Spiritual Expertise"
      subtitle="Define your fields of mastery and instructional specializations to help seekers find your guidance."
      prev={{
        kind: 'link',
        label: 'Back',
        to: '/qari/onboarding/identity',
      }}
      next={{
        kind: 'link',
        label: 'Next Phase',
        to: '/qari/onboarding/verification',
      }}
      autosave={{ status: form.status, flush: form.flush }}
      hint="Next phase: document verification. Your work is saved as you go."
      extras={<InfoCards />}
    >
      <div className="space-y-8">
        {/* Primary mastery */}
        <Field label="Primary Mastery">
          <SelectInput
            value={form.value.primary_discipline ?? ''}
            onChange={(v) =>
              form.patch({ primary_discipline: v || null })
            }
          >
            <option value="">Select your core discipline</option>
            {PRIMARY_MASTERY.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </SelectInput>
        </Field>

        {/* Years of experience */}
        <Field label="Years of Experience">
          <SelectInput
            value={
              form.value.years_experience !== null &&
              form.value.years_experience !== undefined
                ? String(form.value.years_experience)
                : ''
            }
            onChange={(v) =>
              form.patch({
                years_experience: v === '' ? null : Number(v),
              })
            }
          >
            <option value="">Select your experience level</option>
            {YEARS_OPTIONS.map((y) => (
              <option key={y.value} value={String(y.value)}>
                {y.label}
              </option>
            ))}
          </SelectInput>
        </Field>

        {/* Certifications */}
        <Field
          label="Certifications & Ijazah"
          hint="Formal qualifications only"
        >
          <TextArea
            value={form.value.certifications ?? ''}
            onChange={(v) =>
              form.patch({ certifications: v.slice(0, CERT_MAX) })
            }
            rows={4}
            maxLength={CERT_MAX}
            placeholder="E.g., Ijazah in Hafs 'an 'Asim from Sheikh [Name], Masters in Islamic Sciences from [University]…"
          />
        </Field>

        {/* Teaching style chips */}
        <Field label="Teaching Style">
          <div className="flex flex-wrap gap-3 pt-1">
            {TEACHING_STYLE_ORDER.map((s) => {
              const active = (form.value.teaching_styles ?? []).includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStyle(s)}
                  aria-pressed={active}
                  className={cn(
                    'rounded-full border px-5 py-1.5 text-xs font-semibold uppercase tracking-wide transition-all',
                    active
                      ? 'border-transparent bg-beam-soft text-beam-ink shadow-[0_0_15px_rgba(117,213,226,0.4)]'
                      : 'border-night-500/50 text-night-200 hover:border-beam/60 hover:text-beam-pale',
                  )}
                >
                  {QARI_TEACHING_STYLE_LABELS[s]}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Specializations */}
        <Field label="Specializations">
          <div className="grid grid-cols-2 gap-3 pt-1 sm:grid-cols-3">
            {SPECIALIZATIONS.map((s) => {
              const checked = (form.value.specializations ?? []).includes(s);
              return (
                <SpecializationCheckbox
                  key={s}
                  label={s}
                  checked={checked}
                  onChange={() => toggleSpecialization(s)}
                />
              );
            })}
          </div>
        </Field>
      </div>
    </WizardShell>
  );
}

/* ---- Specialization checkbox ---- */

function SpecializationCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="group flex cursor-pointer items-center gap-3">
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
          checked
            ? 'border-beam bg-beam text-beam-ink'
            : 'border-night-500/60 bg-night-900/60 group-hover:border-beam/60',
        )}
        aria-hidden="true"
      >
        {checked && (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3.5 w-3.5"
          >
            <path d="m5 12 5 5L20 7" />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <span
        className={cn(
          'text-sm transition-colors',
          checked
            ? 'text-night-50'
            : 'text-night-200 group-hover:text-beam-pale',
        )}
      >
        {label}
      </span>
    </label>
  );
}

/* ---- Supportive info cards (bottom of design) ---- */

function InfoCards() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <InfoCard
        icon={<IconShield className="h-5 w-5" />}
        title="Data Privacy"
        body="Your certifications are encrypted and used only for internal verification to maintain platform integrity."
        accent="beam"
      />
      <InfoCard
        icon={<IconSparkle className="h-5 w-5" />}
        title="Smart Matching"
        body="Defining your style accurately helps us route the right students to you — better fit, better retention."
        accent="beam-soft"
      />
    </div>
  );
}

function InfoCard({
  icon,
  title,
  body,
  accent,
}: {
  icon: ReactNode;
  title: string;
  body: string;
  accent: 'beam' | 'beam-soft';
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-night-500/30 bg-night-800/60 p-5 backdrop-blur-md',
        accent === 'beam' ? 'border-l-4 border-l-beam/60' : 'border-l-4 border-l-beam-soft/60',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            accent === 'beam' ? 'bg-beam/10 text-beam' : 'bg-beam-soft/15 text-beam-soft',
          )}
        >
          {icon}
        </span>
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-night-50">
            {title}
          </h4>
          <p className="mt-1.5 text-[13px] leading-relaxed text-night-200">
            {body}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---- Profile <-> form bridging ---- */

function toFormValue(profile: QariProfile): FormValue {
  return {
    primary_discipline: profile.primary_discipline,
    years_experience: profile.years_experience,
    certifications: profile.certifications,
    teaching_styles: profile.teaching_styles ?? [],
    specializations: profile.specializations ?? [],
  };
}

function normalizePatch(v: FormValue): Partial<QariProfileDraft> {
  const out: Partial<QariProfileDraft> = {};
  if (v.primary_discipline !== undefined)
    out.primary_discipline = nullableText(v.primary_discipline);
  if (v.years_experience !== undefined)
    out.years_experience = v.years_experience;
  if (v.certifications !== undefined)
    out.certifications = nullableText(v.certifications);
  if (v.teaching_styles !== undefined)
    out.teaching_styles = v.teaching_styles ?? [];
  if (v.specializations !== undefined)
    out.specializations = v.specializations ?? [];
  return out;
}
